import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import type { ScoreBandBucket } from '../../../types/insightsTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ScoreDistributionChartProps {
  /** Live score band data from useInsightsData */
  bands?: ScoreBandBucket[];
}

const ScoreDistributionChart: React.FC<ScoreDistributionChartProps> = ({ bands }) => {
  const buckets = bands ?? [];

  if (buckets.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-slate-400 text-sm">
        No distribution data available yet.
      </div>
    );
  }

  const data = {
    labels: buckets.map((b) => b.band),
    datasets: [
      {
        label: 'Member Count',
        data: buckets.map((b) => b.member_count),
        backgroundColor: '#8b5cf6',
        borderColor: '#8b5cf6',
        borderWidth: 0,
        yAxisID: 'y'
      },
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: '#64748b',
          font: { size: 12 },
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          afterLabel: function(context: any) {
            const bucket = buckets[context.dataIndex];
            if (!bucket) return '';
            return `Avg Score: ${bucket.avg_score?.toFixed(1) ?? '—'}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { size: 11 } }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: '#94a3b8',
          font: { size: 11 },
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      }
    }
  };

  return (
    <div className="h-80">
      <Bar data={data} options={options} />
    </div>
  );
};

export default ScoreDistributionChart;